import React, { useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle2, Loader2, MessageCircle, Phone, X } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { authHeader } from "@/utils/userAuth";
import { extractApiError } from "@/utils/apiError";
import { getMetaCookies, trackLead } from "@/utils/pixel";
import { sanitizePhoneInput, PHONE_MAX_DIGITS } from "@/utils/phoneInput";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Two-step dialog:
 *  Step 1 — consent + "Conferma e invia" → fires /leads/request-l2-info IMMEDIATELY
 *           (automation + Meta CAPI + WA drip start, regardless of step 2).
 *  Step 2 — phone re-entry / confirmation → POST /users/me/phone (optional).
 *
 * The dialog is bypassed entirely when the user already has marketing consent
 * (`consent_call`): the parent fires the API directly without opening this popup.
 */
export default function RequestL2InfoDialog({ open, onClose, onSubmitted, fireTrack, currentPhone }) {
  const [step, setStep] = useState(1);
  const [consent, setConsent] = useState(false);
  const [phone, setPhone] = useState(currentPhone || "");
  const [submitting, setSubmitting] = useState(false);
  const [savingPhone, setSavingPhone] = useState(false);
  const [error, setError] = useState("");

  // Reset state when the dialog is opened.
  useEffect(() => {
    if (open) {
      setStep(1);
      setConsent(false);
      setPhone(currentPhone || "");
      setSubmitting(false);
      setSavingPhone(false);
      setError("");
    }
  }, [open, currentPhone]);

  const handleConfirm = async () => {
    if (!consent || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      // 🔥 Automation + Meta CAPI + WhatsApp drip start NOW.
      const { fbp, fbc } = getMetaCookies();
      const { data } = await axios.post(`${API}/leads/request-l2-info`, {
        consent_call: true,
        fbp, fbc,
        landing_page_url: window.location.href,
      }, { headers: authHeader() });
      try { fireTrack?.("l2_info_requested"); } catch { /* noop */ }
      // Browser Pixel `Lead` event paired with the server-side CAPI event via
      // the same `event_id` — Meta dedupes automatically. Fires only if the
      // pixel is loaded (consent given & script initialised).
      if (data?.event_id) trackLead(data.event_id);
      // Notify parent so the upstream CTA can transition to "Richiesta inviata".
      onSubmitted?.();
      // Move to phone-confirmation step (optional — closing here would still leave
      // the automation running with the existing phone number).
      setStep(2);
    } catch (err) {
      setError(extractApiError(err, "Impossibile inviare la richiesta. Riprova."));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSavePhone = async () => {
    if (savingPhone) return;
    setSavingPhone(true);
    setError("");
    try {
      if (phone && phone !== currentPhone) {
        await axios.post(`${API}/users/me/phone`, { phone }, { headers: authHeader() });
      }
      onClose?.();
    } catch (err) {
      setError(extractApiError(err, "Numero non valido. Controlla e riprova."));
    } finally {
      setSavingPhone(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !submitting && !savingPhone) onClose?.(); }}>
      <DialogContent
        className="sm:max-w-[480px] rounded-2xl border-0 p-0 overflow-hidden bg-white"
        data-testid="request-l2-info-dialog"
      >
        <div className="h-2 bg-tension w-full" />
        <div className="px-7 pt-7 pb-7">
          {step === 1 ? (
            <Step1
              consent={consent}
              setConsent={setConsent}
              submitting={submitting}
              error={error}
              onCancel={() => { if (!submitting) onClose?.(); }}
              onConfirm={handleConfirm}
            />
          ) : (
            <Step2
              phone={phone}
              setPhone={setPhone}
              saving={savingPhone}
              error={error}
              onSkip={() => onClose?.()}
              onSave={handleSavePhone}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Step1({ consent, setConsent, submitting, error, onCancel, onConfirm }) {
  return (
    <>
      <DialogHeader className="space-y-2 text-center">
        <DialogTitle
          className="text-2xl md:text-[26px] leading-tight"
          style={{ fontFamily: "Merriweather, serif", color: "var(--np-primary)" }}
        >
          Richiedi informazioni per la Lezione 2
        </DialogTitle>
        <DialogDescription className="text-[15px] leading-relaxed" style={{ color: "var(--np-muted)" }}>
          Lasciaci il consenso a contattarti e ti scriveremo al più presto su WhatsApp.
        </DialogDescription>
      </DialogHeader>

      <ul className="mt-6 space-y-2.5 text-[14px]" style={{ color: "var(--np-primary)" }}>
        <li className="flex items-start gap-2.5">
          <MessageCircle size={16} style={{ color: "var(--np-secondary)", marginTop: 2 }} />
          <span>Riceverai un messaggio WhatsApp al più presto</span>
        </li>
        <li className="flex items-start gap-2.5">
          <CheckCircle2 size={16} style={{ color: "var(--np-secondary)", marginTop: 2 }} />
          <span>Nessun impegno — solo qualche domanda per orientarti</span>
        </li>
      </ul>

      <label
        className="mt-7 flex items-start gap-3 cursor-pointer p-4 rounded-lg border-2 transition"
        style={{
          borderColor: consent ? "var(--np-secondary)" : "#e2e8f0",
          background: consent ? "rgba(213,93,57,0.05)" : "#fafafa",
        }}
      >
        <Checkbox
          checked={consent}
          onCheckedChange={(v) => setConsent(!!v)}
          data-testid="l2-info-consent-checkbox"
          className="mt-0.5"
          disabled={submitting}
        />
        <span className="text-[14px] leading-snug" style={{ color: "var(--np-primary)" }}>
          <strong>Accetto</strong> di essere contattato <strong>via WhatsApp</strong> per ricevere informazioni sul percorso.
        </span>
      </label>

      {error && (
        <p className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="l2-info-error">
          {error}
        </p>
      )}

      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        <button
          type="button"
          onClick={onCancel}
          data-testid="l2-info-cancel-btn"
          disabled={submitting}
          className="cta-btn cta-btn--ghost-light flex-1 disabled:opacity-50"
        >
          <X size={16} /> Annulla
        </button>
        <button
          type="button"
          onClick={onConfirm}
          data-testid="l2-info-submit-btn"
          disabled={!consent || submitting}
          className="cta-btn flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? (
            <><Loader2 size={16} className="animate-spin" /> Invio…</>
          ) : (
            <>Conferma e invia</>
          )}
        </button>
      </div>
    </>
  );
}

function Step2({ phone, setPhone, saving, error, onSkip, onSave }) {
  return (
    <>
      <DialogHeader className="space-y-2 text-center">
        <div className="flex justify-center mb-2">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center"
            style={{ background: "rgba(213,93,57,0.12)", border: "1px solid rgba(213,93,57,0.30)" }}
          >
            <CheckCircle2 size={24} style={{ color: "var(--np-secondary)" }} />
          </div>
        </div>
        <DialogTitle
          className="text-2xl md:text-[26px] leading-tight"
          style={{ fontFamily: "Merriweather, serif", color: "var(--np-primary)" }}
        >
          Richiesta ricevuta ✨
        </DialogTitle>
        <DialogDescription className="text-[15px] leading-relaxed" style={{ color: "var(--np-muted)" }}>
          Conferma il numero su cui ti scriveremo (o correggilo se sbagliato).
        </DialogDescription>
      </DialogHeader>

      <div className="mt-6">
        <label htmlFor="l2-phone-confirm" className="text-[13px] font-semibold tracking-wide uppercase block mb-1.5"
          style={{ color: "var(--np-primary)" }}>
          Il tuo numero WhatsApp
        </label>
        <div className="relative">
          <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--np-muted)" }} />
          <input
            id="l2-phone-confirm"
            data-testid="l2-info-phone-input"
            type="tel"
            inputMode="tel"
            value={phone}
            onChange={(e) => setPhone(sanitizePhoneInput(e.target.value))}
            maxLength={PHONE_MAX_DIGITS + 6}
            placeholder="+39 333 1234567"
            className="w-full h-11 pl-10 pr-3 rounded-lg border focus-visible:ring-2"
            style={{ borderColor: "#dbe3ec" }}
            autoFocus
          />
        </div>
      </div>

      {error && (
        <p className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="l2-info-error">
          {error}
        </p>
      )}

      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        <button
          type="button"
          onClick={onSkip}
          data-testid="l2-info-skip-phone-btn"
          disabled={saving}
          className="cta-btn cta-btn--ghost-light flex-1 disabled:opacity-50"
        >
          Salta
        </button>
        <button
          type="button"
          onClick={onSave}
          data-testid="l2-info-confirm-phone-btn"
          disabled={saving || !phone}
          className="cta-btn flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? (
            <><Loader2 size={16} className="animate-spin" /> Salvo…</>
          ) : (
            <>Conferma il numero</>
          )}
        </button>
      </div>
    </>
  );
}
